/**
 * TrueLayer OAuth Callback Page
 *
 * Handles the OAuth redirect from TrueLayer after bank authentication.
 * Exchanges the authorization code for tokens and syncs bank accounts.
 */

import { useEffect, useState } from 'react';
import { useSearchParams, useNavigate } from 'react-router-dom';
import { useCompleteBankConnection } from '@business-assistant/shared';
import './GoogleOAuthCallback.css'; // Reuse the same styles

type CallbackStatus = 'processing' | 'syncing' | 'success' | 'error';

export function TrueLayerOAuthCallback() {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const [status, setStatus] = useState<CallbackStatus>('processing');
  const [errorMessage, setErrorMessage] = useState<string>('');
  const [accountsCount, setAccountsCount] = useState<number>(0);

  const completeBankConnection = useCompleteBankConnection();

  useEffect(() => {
    const code = searchParams.get('code');
    const state = searchParams.get('state');
    const error = searchParams.get('error');
    const errorDescription = searchParams.get('error_description');

    // Handle error from TrueLayer
    if (error) {
      setStatus('error');
      setErrorMessage(errorDescription || error);

      // Send error to parent if in popup
      if (window.opener) {
        window.opener.postMessage(
          {
            type: 'truelayer-oauth-callback',
            error: errorDescription || error,
          },
          window.location.origin
        );
        setTimeout(() => window.close(), 3000);
      }
      return;
    }

    // Validate required params
    if (!code || !state) {
      setStatus('error');
      setErrorMessage('Parametri di autorizzazione mancanti');
      return;
    }

    // Complete the bank connection
    handleCompleteConnection(code, state);
  }, [searchParams]);

  async function handleCompleteConnection(code: string, state: string) {
    setStatus('syncing');

    try {
      const result = await completeBankConnection.mutateAsync({
        authCode: code,
        state: state,
        provider: 'truelayer',
      });

      if (result.success) {
        setStatus('success');
        setAccountsCount(result.accounts?.length || 0);

        // Send success to parent if in popup
        if (window.opener) {
          window.opener.postMessage(
            {
              type: 'truelayer-oauth-callback',
              success: true,
              connection: result.connection,
              accounts: result.accounts,
            },
            window.location.origin
          );
          setTimeout(() => window.close(), 2000);
        } else {
          // Not in popup - redirect to accounting/banking after delay
          setTimeout(() => {
            navigate('/accounting', { replace: true });
          }, 2000);
        }
      } else {
        throw new Error(result.errors?.[0]?.message || 'Connessione fallita');
      }
    } catch (err) {
      setStatus('error');
      setErrorMessage(err instanceof Error ? err.message : 'Errore durante la connessione');

      // Send error to parent if in popup
      if (window.opener) {
        window.opener.postMessage(
          {
            type: 'truelayer-oauth-callback',
            error: err instanceof Error ? err.message : 'Connection failed',
          },
          window.location.origin
        );
        setTimeout(() => window.close(), 3000);
      }
    }
  }

  return (
    <div className="oauth-callback">
      <div className="oauth-callback-content">
        {status === 'processing' && (
          <>
            <div className="oauth-callback-spinner" />
            <h2>Elaborazione...</h2>
            <p>Stiamo verificando l'autorizzazione dalla tua banca.</p>
          </>
        )}

        {status === 'syncing' && (
          <>
            <div className="oauth-callback-spinner" />
            <h2>Sincronizzazione conti...</h2>
            <p>Stiamo importando i tuoi conti bancari e il saldo.</p>
          </>
        )}

        {status === 'success' && (
          <>
            <div className="oauth-callback-icon oauth-callback-icon--success">
              <svg viewBox="0 0 24 24" width="48" height="48" fill="none" stroke="currentColor" strokeWidth="2">
                <path d="M20 6L9 17l-5-5" strokeLinecap="round" strokeLinejoin="round"/>
              </svg>
            </div>
            <h2>Banca collegata!</h2>
            <p>
              {accountsCount > 0
                ? `${accountsCount} ${accountsCount === 1 ? 'conto importato' : 'conti importati'} con successo.`
                : 'Connessione completata con successo.'}
            </p>
            {!window.opener && (
              <p style={{ marginTop: '0.5rem', color: 'var(--text-muted)' }}>
                Reindirizzamento in corso...
              </p>
            )}
          </>
        )}

        {status === 'error' && (
          <>
            <div className="oauth-callback-icon oauth-callback-icon--error">
              <svg viewBox="0 0 24 24" width="48" height="48" fill="none" stroke="currentColor" strokeWidth="2">
                <circle cx="12" cy="12" r="10"/>
                <path d="M15 9l-6 6M9 9l6 6" strokeLinecap="round"/>
              </svg>
            </div>
            <h2>Connessione fallita</h2>
            <p>{errorMessage || 'Si è verificato un errore durante la connessione.'}</p>
            {!window.opener && (
              <button
                className="oauth-callback-btn"
                onClick={() => navigate('/accounting')}
              >
                Torna alla contabilità
              </button>
            )}
          </>
        )}
      </div>
    </div>
  );
}

export default TrueLayerOAuthCallback;
